import { Link } from "react-router-dom";
import { can, signInURL } from "../api";
import { useT } from "../i18n";
import { primaryOrg, useSession } from "../session";
import { useSiteScheme } from "../siteTheme";
import AccountMenu from "./AccountMenu";
import { Lockup } from "./Logo";
import Placeholder from "./Placeholder";

/**
 * Header is the session-aware bar. It renders three states — loading, signed
 * out, signed in — and never a fourth: a header that shows "Sign in" for a
 * moment to someone who is already signed in reads as a dropped session.
 */
export default function Header() {
  const { me, isPending } = useSession();
  const org = primaryOrg(me);
  const t = useT();

  return (
    <header className="sticky top-0 z-20 border-b border-line bg-ground/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3 sm:gap-4">
        <Link to="/" className="flex items-center rounded-control" aria-label={t.nav.home}>
          <Lockup />
        </Link>

        <nav className="flex-1 flex items-center gap-1">
          {/* The catalogue is always reachable: / is the reader's own shelf
              now, and a shelf with no way to the books is a dead end. */}
          <Link
            to="/browse"
            data-testid="nav-browse"
            className="rounded-control px-2 py-1 text-sm text-ink-soft transition-colors hover:bg-sunk hover:text-ink"
          >
            {t.nav.browse}
          </Link>
          {me && can(org, "book.read_draft") && (
            <Link
              to="/studio"
              data-testid="nav-studio"
              className="rounded-control px-2 py-1 text-sm text-ink-soft transition-colors hover:bg-sunk hover:text-ink"
            >
              {t.nav.studio}
            </Link>
          )}
        </nav>

        <SchemeToggle />

        {/* The same footprint as the avatar, so nothing shifts when it loads. */}
        {isPending && <Placeholder className="size-9 rounded-full" />}

        {/* No language switch here (owner, 2026-09-17). The interface language
            is chosen on /settings, which a visitor can reach as well, and by an
            admin for an account; offering Persian in the header put a language
            nobody had asked for one tap from every page, on a product whose
            commonest content language is not its only one. */}
        {!isPending && !me && (
          <a
            href={signInURL()}
            data-testid="sign-in"
            className="rounded-control bg-sage px-3.5 py-1.5 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep"
          >
            {t.nav.signIn}
          </a>
        )}

        {!isPending && me && <AccountMenu me={me} />}
      </div>
    </header>
  );
}

/**
 * SchemeToggle is the sun/moon (owner, 2026-10-22): one press switches the
 * site between light and dark and is remembered on this device. It shows what
 * it switches to — a moon on a light page — and its label says so.
 */
function SchemeToggle() {
  const t = useT();
  const [scheme, toggle] = useSiteScheme();
  const dark = scheme === "dark";
  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="scheme-toggle"
      data-scheme={scheme}
      aria-label={dark ? t.nav.toLight : t.nav.toDark}
      title={dark ? t.nav.toLight : t.nav.toDark}
      className="grid size-9 place-items-center rounded-full text-ink-soft transition-colors hover:bg-sunk hover:text-ink"
    >
      <svg
        viewBox="0 0 24 24"
        className="size-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {dark ? (
          <>
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
          </>
        ) : (
          <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5Z" />
        )}
      </svg>
    </button>
  );
}
